import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { GroupedBarChartComponent } from '../../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { AuthService, FilterService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatCost } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { MaintenanceInsightsResponse } from '../../../models/maintenance-dashboard-response.model';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { PreventiveVsReactiveModalComponent } from '../../modals/preventive-vs-reactive-modal/preventive-vs-reactive-modal.component';
import { Subject } from 'rxjs';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-preventive-vs-reactive',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, GroupedBarChartComponent, EmptyStateComponent],
  templateUrl: './preventive-vs-reactive.component.html',
  styleUrl: './preventive-vs-reactive.component.scss'
})
export class PreventiveVsReactiveComponent implements OnInit, OnDestroy {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private modalService = inject(NgbModal);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private authService = inject(AuthService);

  readonly PREVENTIVE_SERIES = 'Preventive';
  readonly REACTIVE_SERIES = 'Reactive';
  chartData: GroupedChartData[] = [];
  readonly barWidth = 12;
  readonly chartHeight = 175;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  subgroups = this.buildSubgroups();

  title: string = this.transloco.translate('maintenanceDashboard.charts.preventiveVsReactive');
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.transloco.selectTranslation()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.subgroups = this.buildSubgroups();
        this.title = this.transloco.translate('maintenanceDashboard.charts.preventiveVsReactive');
      });

    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadMaintenanceData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.authService.getUserSettings()?.currencyCode ?? 'ZAR');

    // Only load data if filter is initialized
    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadMaintenanceData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onFilterChangeAsync(): Promise<void> {
    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadMaintenanceData();
    }
  }

  private async loadMaintenanceData(): Promise<void> {
    this.isLoading = true;
    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const [summaryResponse, preventiveResponse, reactiveResponse] = await Promise.all([
        this.dashboardService.getPreventiveVsReactiveSummary({
          ...request,
          previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
          previousTimeTo: this.widgetFilter.previousCycleEndDate!
        }, this.cancelSubject),
        this.dashboardService.getPreventiveRatio(request, this.cancelSubject),
        this.dashboardService.getReactiveRatio(request, this.cancelSubject)
      ]);

      this.totalValue = summaryResponse.data?.totalValue ?? 0;
      this.percentageChange = summaryResponse.data?.percentageChange ?? 0;
      this.chartData = this.processResponses(preventiveResponse.data ?? [], reactiveResponse.data ?? []);
      this.yTicks = this.calculateYTicks(this.chartData);

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private processResponses(
    preventiveData: MaintenanceInsightsResponse[],
    reactiveData: MaintenanceInsightsResponse[]
  ): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { data: preventiveData, label: this.PREVENTIVE_SERIES },
      { data: reactiveData, label: this.REACTIVE_SERIES }
    ];

    switch (groupingType) {
      case 'hourly': return TimeSeriesGroupingHelper.groupByHours(datasets);
      case 'daily': return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
      default: return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item[this.PREVENTIVE_SERIES]) || 0,
      Number(item[this.REACTIVE_SERIES]) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks);
  }

  private buildSubgroups(): Array<{ name: string; legend: string; color: string }> {
    return [
      { name: this.PREVENTIVE_SERIES, legend: this.transloco.translate('maintenanceDashboard.charts.preventive'), color: '#66AECF' },
      { name: this.REACTIVE_SERIES, legend: this.transloco.translate('maintenanceDashboard.charts.reactive'), color: '#F4BC2B' }
    ];
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return true;
  }

  formatValue(): string {
    return formatCost(this.totalValue, this.currencySymbol);
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      // Check all subgroups for non-zero values
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(PreventiveVsReactiveModalComponent, modalOptions);
  }
}